/**
 * Sprint V98 · Free-Dashboard Banner "Kostenfreies Live-Webinar".
 * Sichtbar an: Dashboard-Home, Muster, Profil.
 * Sichtbarkeitsregel:
 *   - Nur wenn User Free-Tier ist
 *   - Nur wenn Webinar in Zukunft liegt
 *   - Wenn User bereits registriert -> "Du bist dabei"-Variante
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { isFree } from "@/lib/licensing";

const WEBINAR_ID = "1";  // Stage 1: hardcoded auf Webinar 1

function fmtDateShort(iso) {
  try {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const p = fmt.formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    return `${p.weekday}, ${p.day}.${p.month}. · ${p.hour}:${p.minute} Uhr`;
  } catch {
    return iso;
  }
}

export function FreeWebinarBanner({ user, className = "", variant = "default" }) {
  const [config, setConfig] = useState(null);

  // Kanonischer Free-Check via /app/frontend/src/lib/licensing.js
  // (matcht Backend-Flag `user.is_premium`).
  const showForUser = isFree(user);

  useEffect(() => {
    if (!showForUser) return;
    api.get(`/webinar/${WEBINAR_ID}`).then((r) => setConfig(r.data)).catch(() => {});
  }, [showForUser]);

  if (!showForUser) return null;
  if (!config) return null;
  // Sprint V101.c — Banner via Admin-Panel deaktivierbar.
  if (config.banner_enabled === false) return null;

  // Zukunfts-Check
  let daysUntil = "";
  try {
    const ts = new Date(config.event_datetime).getTime();
    if (ts < Date.now()) return null;
    const days = Math.max(1, Math.ceil((ts - Date.now()) / (1000 * 60 * 60 * 24)));
    daysUntil = days === 1 ? "1 Tag" : `${days} Tagen`;
  } catch { return null; }

  const dateShort = fmtDateShort(config.event_datetime);

  return (
    <Link
      to={`/webinar/${WEBINAR_ID}/anmeldung`}
      data-testid="free-webinar-banner"
      data-variant={variant}
      className={`group block rounded-xl sm:rounded-2xl border sm:border-2 border-[#F3651D]/40 bg-gradient-to-r from-[#F3651D]/10 via-white to-[#4563BB]/10 dark:from-[#F3651D]/20 dark:via-slate-800 dark:to-[#4563BB]/20 hover:border-[#F3651D] hover:shadow-lg transition-all p-3 sm:p-7 relative overflow-hidden ${className}`}
    >
      {/* Dezente Pulse-Glow im Hintergrund — nur ab sm sichtbar */}
      <span
        aria-hidden="true"
        className="hidden sm:block absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#F3651D]/20 blur-3xl group-hover:bg-[#F3651D]/30 transition-colors"
      />
      <div className="relative flex items-center gap-3 sm:gap-4">
        <div className="flex-shrink-0 w-9 h-9 sm:w-14 sm:h-14 rounded-full bg-[#F3651D] text-white flex items-center justify-center sm:shadow-lg sm:ring-4 sm:ring-[#F3651D]/20">
          <CalendarClock className="w-4 h-4 sm:w-7 sm:h-7" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider sm:tracking-widest text-[#F3651D] leading-tight truncate">
            Live-Webinar &middot; in {daysUntil}
          </p>
          {/* Sprint V100.4 (17.09.2026) — Outcome-driven Hauptzeile statt
              abstraktem Webinar-Titel. Der Titel selbst ist zwar in der DB
              hinterlegt, hilft dem User im 1s-Scan aber nicht. */}
          <p className="mt-0.5 sm:mt-1 text-sm sm:text-2xl font-bold sm:font-extrabold text-slate-900 dark:text-white leading-tight truncate sm:whitespace-normal">
            Nutze dein volles Potenzial.
          </p>
          <p className="hidden sm:block mt-1 text-sm sm:text-base text-slate-600 dark:text-slate-300">
            {config.title} &middot; {dateShort}
          </p>
          {/* V100+ Iteration 4b (18.09.2026) — Wirkung statt „klarer zeigen"
              (Tautologie). Kern der Positionierung: bewusste Wirkung. */}
          <p className="sm:hidden mt-0.5 text-[11px] text-slate-600 dark:text-slate-300 truncate">
            Wie du deine Wirkung bewusst gestaltest.
          </p>
        </div>
        <div className="flex-shrink-0 inline-flex items-center gap-1 sm:gap-2 rounded-lg sm:rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white px-2 py-1.5 sm:px-5 sm:py-3 text-xs sm:text-base font-bold sm:shadow-md sm:group-hover:shadow-lg transition-shadow whitespace-nowrap" aria-label="Kostenfrei dabei sein">
          <span className="hidden sm:inline">Kostenfrei dabei sein</span>
          <ArrowRight className="w-4 h-4 sm:w-4 sm:h-4 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default FreeWebinarBanner;
